import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DndContext,
  useSensors,
  useSensor,
  PointerSensor,
  DragOverlay,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';

import { Grid, Row, Col, UnsavedChangesModal } from 'modules/Core/Common';
import {
  WidgetsListHeader,
  WidgetsSidebar,
} from 'modules/Dashboard/Widgets/Common';
import { WidgetsList } from './WidgetsList.component';
import { useAuth } from 'modules/Auth/Hooks';
import { useWidgets } from 'modules/Dashboard/Widgets/Hooks';
import { isFalsy } from 'modules/Utils';
import { WIDGETS_LIST_DROPPABLE_CONTAINER } from 'modules/Dashboard/Widgets/WidgetsDragAndDrop.utils';
import { SortableWidgetContainerItem } from './SortableWidgetContainerItem.component';

export const WidgetsListContainer = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeDragItemId, setActiveDragItemId] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocationRef = useRef(location.pathname);
  const isNavigatingBackRef = useRef(false);
  const targetLocationRef = useRef(null);

  const { userWidgets, isSuperAdmin } = useAuth();
  const {
    state,
    setActiveWidgets,
    addActiveWidget,
    removeActiveWidget,
    setUnsavedChanges,
    saveWidgets,
  } = useWidgets();

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (state?.unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state?.unsavedChanges]);

  useEffect(() => {
    const handleLinkClick = (e) => {
      if (!state?.unsavedChanges) return;

      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
      ) {
        return;
      }

      // Check if it's an internal route
      try {
        const url = new URL(href, window.location.origin);
        if (
          url.origin === window.location.origin &&
          url.pathname !== location.pathname
        ) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNavigation(url.pathname);
          setShowUnsavedModal(true);
        }
      } catch {
        // Invalid URL, let it proceed
      }
    };

    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [state?.unsavedChanges, location.pathname]);

  useEffect(() => {
    if (isNavigatingBackRef.current) {
      isNavigatingBackRef.current = false;
      previousLocationRef.current = location.pathname;
      return;
    }

    if (!state?.unsavedChanges) {
      previousLocationRef.current = location.pathname;
      return;
    }

    if (previousLocationRef.current !== location.pathname) {
      targetLocationRef.current = location.pathname;
      setShowUnsavedModal(true);
      isNavigatingBackRef.current = true;
      navigate(previousLocationRef.current, { replace: true });
    }
  }, [location.pathname, state?.unsavedChanges, navigate]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (userWidgets?.widgets) {
      setActiveWidgets(userWidgets.widgets);
    }
  }, [userWidgets?.widgets]);

  const handleDragStart = (event) => {
    const { active } = event;

    setIsDragging(true);
    setActiveDragItemId(active.id);
  };

  const handleDragEnd = (event) => {
    setIsDragging(false);
    setActiveDragItemId(null);

    const { active, over } = event;

    if (typeof active.id === 'number' && typeof over?.id === 'number') {
      if (active.id === over.id) return;

      const activeIndex = state?.data?.activeWidgets?.findIndex(
        (widget) => widget.id === active.id
      );
      const overIndex = state?.data?.activeWidgets?.findIndex(
        (widget) => widget.id === over.id
      );

      setActiveWidgets(
        arrayMove(state?.data?.activeWidgets, activeIndex, overIndex)
      );
      setUnsavedChanges(true);
    }

    if (over?.id === WIDGETS_LIST_DROPPABLE_CONTAINER) {
      const { active } = event;

      if (isFalsy(active.data.current)) return;

      addActiveWidget(active.data.current);
    }
  };

  const activeDragItemWidget = state?.data?.activeWidgets?.find(
    (widget) => widget.id === activeDragItemId
  );

  const handleConfirmNavigate = () => {
    setUnsavedChanges(false);
    setShowUnsavedModal(false);
    const targetPath = pendingNavigation || targetLocationRef.current;
    if (targetPath && targetPath !== location.pathname) {
      navigate(targetPath);
    }
    setPendingNavigation(null);
    targetLocationRef.current = null;
  };

  const handleCancelNavigate = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
    targetLocationRef.current = null;
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      modifiers={[restrictToWindowEdges]}
    >
      <Grid fluid>
        <WidgetsListHeader
          onAddWidget={() => setIsSidebarOpen(true)}
          loading={state?.loading}
          onSaveDashboard={saveWidgets}
        />
        <Row>
          <Col xs={12}>
            <WidgetsList
              activeWidgets={state?.data?.activeWidgets}
              activeDragItemWidget={activeDragItemWidget}
              isDragging={isDragging}
            />
          </Col>
        </Row>
        <WidgetsSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          activeWidgets={state?.data?.activeWidgets}
          onAddActiveWidget={addActiveWidget}
          onRemoveActiveWidget={removeActiveWidget}
          isDragging={isDragging}
          isSuperAdmin={isSuperAdmin}
        />
      </Grid>
      <DragOverlay>
        {activeDragItemId && activeDragItemWidget ? (
          <SortableWidgetContainerItem widget={activeDragItemWidget} />
        ) : null}
      </DragOverlay>
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onClose={handleCancelNavigate}
        onConfirm={handleConfirmNavigate}
      />
    </DndContext>
  );
};
